import { useState, useEffect } from 'react';
import { MagnifyingGlass, Globe, Buildings, SwimmingPool, Users, Link, CaretDown, CaretRight, PencilSimple, FloppyDisk, Plus, Trash, Briefcase, LinkedinLogo, Gavel, UserCircle } from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_NAMES = {
  NL: 'Nederland', BE: 'Belgie', FR: 'Frankrijk', DE: 'Duitsland', GB: 'Verenigd Koninkrijk',
  IE: 'Ierland', LU: 'Luxemburg', AT: 'Oostenrijk', CH: 'Zwitserland', ES: 'Spanje',
  PT: 'Portugal', IT: 'Italie', GR: 'Griekenland', DK: 'Denemarken', SE: 'Zweden',
  NO: 'Noorwegen', FI: 'Finland', PL: 'Polen', CZ: 'Tsjechie', HU: 'Hongarije', RO: 'Roemenie', US: 'Verenigde Staten',
};

const CATEGORY_CONFIG = [
  { key: 'pool_terms', label: 'Zwembad Termen', icon: SwimmingPool, color: 'text-cyan-600 bg-cyan-50 border-cyan-200' },
  { key: 'hotel_terms', label: 'Hotel / Wellness', icon: Buildings, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { key: 'club_terms', label: 'Clubs / Verenigingen', icon: Users, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { key: 'scraper_queries', label: 'Scraper Zoekopdrachten', icon: MagnifyingGlass, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { key: 'directory_searches', label: 'Directory Zoekopdrachten', icon: Globe, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { key: 'role_queries', label: 'Functie Zoekopdrachten', icon: UserCircle, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  { key: 'supplier_terms', label: 'Leveranciers & Dienstverleners', icon: Briefcase, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { key: 'linkedin_queries', label: 'LinkedIn / Google Operators', icon: LinkedinLogo, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { key: 'tender_queries', label: 'Overheid / Aanbestedingen', icon: Gavel, color: 'text-rose-600 bg-rose-50 border-rose-200' },
  { key: 'search_queries', label: 'Algemene Zoekopdrachten', icon: MagnifyingGlass, color: 'text-zinc-600 bg-zinc-50 border-zinc-200' },
];

const URL_CATEGORIES = [
  { key: 'directory_urls', label: 'Directory URLs' },
  { key: 'federation_urls', label: 'Federatie URLs' },
  { key: 'open_data_urls', label: 'Open Data URLs' },
  { key: 'directory_platforms', label: 'Platform URLs' },
];

export default function SearchTermsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [editTerms, setEditTerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newTerm, setNewTerm] = useState('');

  useEffect(() => {
    fetch(`${API}/search-terms`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleCountry = (code) => setExpanded(prev => ({ ...prev, [code]: !prev[code] }));

  const startEdit = (countryCode, catKey, terms) => {
    setEditing({ countryCode, catKey });
    setEditTerms([...terms]);
    setNewTerm('');
  };

  const cancelEdit = () => { setEditing(null); setEditTerms([]); setNewTerm(''); };

  const addTerm = () => {
    if (newTerm.trim() && !editTerms.includes(newTerm.trim())) {
      setEditTerms(prev => [...prev, newTerm.trim()]);
      setNewTerm('');
    }
  };

  const removeTerm = (idx) => setEditTerms(prev => prev.filter((_, i) => i !== idx));

  const [translating, setTranslating] = useState(false);

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    const { countryCode, catKey } = editing;

    // Include any unsaved term from the input field
    let finalTerms = [...editTerms];
    if (newTerm.trim() && !finalTerms.includes(newTerm.trim())) {
      finalTerms.push(newTerm.trim());
    }

    if (countryCode === 'NL') {
      const updatedNL = { ...data.countries.NL };
      updatedNL[catKey] = finalTerms;
      await fetch(`${API}/search-terms`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nl_master: {
          pool_terms: updatedNL.pool_terms || [],
          hotel_terms: updatedNL.hotel_terms || [],
          club_terms: updatedNL.club_terms || [],
          directory_searches: updatedNL.directory_searches || [],
          search_queries: updatedNL.search_queries || [],
          scraper_queries: updatedNL.scraper_queries || [],
          role_queries: updatedNL.role_queries || [],
          supplier_terms: updatedNL.supplier_terms || [],
          linkedin_queries: updatedNL.linkedin_queries || [],
          tender_queries: updatedNL.tender_queries || [],
        }}),
      });
      setData(prev => ({
        ...prev,
        countries: {
          ...prev.countries,
          NL: { ...prev.countries.NL, [catKey]: finalTerms },
        },
      }));
      setTranslating(true);
      setTimeout(() => setTranslating(false), 30000);
    }
    setSaving(false);
    setEditing(null);
    setEditTerms([]);
    setNewTerm('');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-sm text-zinc-400">Geen data beschikbaar</div>;

  const countries = data.countries || {};
  const contactPatterns = data.contact_page_patterns || [];
  const facilityTypes = data.facility_type_keywords || {};

  // Count total terms
  const totalTerms = Object.values(countries).reduce((sum, c) => {
    return sum + (c.pool_terms?.length || 0) + (c.hotel_terms?.length || 0) + (c.club_terms?.length || 0) +
           (c.search_queries?.length || 0) + (c.directory_searches?.length || 0);
  }, 0);

  return (
    <div className="animate-fade-in" data-testid="search-terms-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Zoektermen</h1>
        <p className="text-sm text-zinc-500 mt-1">Alle zoektermen die worden gebruikt voor het vinden van zwembaden en contactpersonen</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="search-terms-summary">
        <div className="bg-white p-4 text-center">
          <div className="font-data text-2xl font-bold text-zinc-800">{Object.keys(countries).length}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Landen</div>
        </div>
        <div className="bg-white p-4 text-center">
          <div className="font-data text-2xl font-bold text-cyan-600">{totalTerms}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Zoektermen</div>
        </div>
        <div className="bg-white p-4 text-center">
          <div className="font-data text-2xl font-bold text-emerald-600">{contactPatterns.length}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Contact Patronen</div>
        </div>
        <div className="bg-white p-4 text-center">
          <div className="font-data text-2xl font-bold text-purple-600">{Object.keys(facilityTypes).length}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Faciliteitstypen</div>
        </div>
      </div>

      {/* Translation in progress banner */}
      {translating && (
        <div className="border-2 border-indigo-200 bg-indigo-50 p-4 mb-6 flex items-center gap-3" data-testid="translating-banner">
          <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <div>
            <div className="text-sm font-medium text-indigo-800">Vertalingen worden gegenereerd...</div>
            <div className="text-xs text-indigo-600">De Nederlandse zoektermen worden automatisch vertaald naar alle andere talen. Dit kan een minuut duren.</div>
          </div>
        </div>
      )}

      {/* Last translation info */}
      {data.last_translation && (
        <div className="text-xs text-zinc-400 mb-4">
          Laatst vertaald: {new Date(data.last_translation).toLocaleString('nl-NL')}
        </div>
      )}

      {/* Countries */}
      <div className="space-y-2 mb-6" data-testid="countries-list">
        {Object.entries(countries).sort((a, b) => (COUNTRY_NAMES[a[0]] || a[0]).localeCompare(COUNTRY_NAMES[b[0]] || b[0])).map(([code, terms]) => {
          const isExpanded = expanded[code];
          const termCount = (terms.pool_terms?.length || 0) + (terms.hotel_terms?.length || 0) + (terms.club_terms?.length || 0) + (terms.search_queries?.length || 0) + (terms.directory_searches?.length || 0);
          return (
            <div key={code} className="border border-zinc-200 bg-white" data-testid={`country-${code}`}>
              <button onClick={() => toggleCountry(code)}
                className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  {isExpanded ? <CaretDown size={16} className="text-zinc-400" /> : <CaretRight size={16} className="text-zinc-400" />}
                  <span className="text-sm font-bold text-zinc-700 w-8">{code}</span>
                  <span className="text-sm font-medium">{COUNTRY_NAMES[code] || code}</span>
                  <Badge className="text-[10px] bg-zinc-100 text-zinc-500">{terms.language}</Badge>
                  {terms.is_master && <Badge className="text-[10px] bg-blue-100 text-blue-700 border border-blue-200">Master</Badge>}
                  {terms.is_translated && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200">Auto-vertaald</Badge>}
                  {!terms.is_master && !terms.is_translated && <Badge className="text-[10px] bg-zinc-100 text-zinc-400">Standaard</Badge>}
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <span>{termCount} termen</span>
                  <span>{(terms.directory_urls?.length || 0) + (terms.federation_urls?.length || 0) + (terms.open_data_urls?.length || 0)} bronnen</span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-zinc-100 p-4 space-y-4">
                  {/* Term Categories */}
                  {CATEGORY_CONFIG.map(cat => {
                    const catTerms = terms[cat.key] || [];
                    if (catTerms.length === 0) return null;
                    const isEditing = editing?.countryCode === code && editing?.catKey === cat.key;
                    const Icon = cat.icon;
                    return (
                      <div key={cat.key} className={`border p-3 ${cat.color}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon size={14} />
                            <span className="text-xs font-semibold uppercase tracking-wider">{cat.label}</span>
                            <span className="text-[10px] opacity-60">{catTerms.length}</span>
                          </div>
                          {!isEditing && code === 'NL' && (
                            <button onClick={() => startEdit(code, cat.key, catTerms)}
                              className="text-xs flex items-center gap-1 opacity-60 hover:opacity-100" data-testid={`edit-${code}-${cat.key}`}>
                              <PencilSimple size={12} /> Bewerken
                            </button>
                          )}
                          {!isEditing && code !== 'NL' && terms.is_translated && (
                            <span className="text-[10px] text-emerald-500 italic">vertaald vanuit NL</span>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-1.5">
                              {editTerms.map((term, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-xs bg-white px-2 py-1 border border-zinc-200">
                                  {term}
                                  <button onClick={() => removeTerm(i)} className="text-red-400 hover:text-red-600"><Trash size={10} /></button>
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input value={newTerm} onChange={e => setNewTerm(e.target.value)} placeholder="Nieuwe term..."
                                className="text-xs h-8" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTerm(); }}} />
                              <button onClick={addTerm} className="text-xs px-2 h-8 bg-white border border-zinc-200 hover:bg-zinc-50"><Plus size={12} /></button>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={saveEdit} disabled={saving} data-testid={`save-${code}-${cat.key}`}
                                className="text-xs px-3 py-1.5 bg-[#0055FF] text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                                <FloppyDisk size={12} /> {saving ? 'Opslaan...' : 'Opslaan'}
                              </button>
                              <button onClick={cancelEdit} className="text-xs px-3 py-1.5 border border-zinc-200 hover:bg-zinc-50">Annuleren</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {catTerms.map((term, i) => (
                              <span key={i} className="text-xs bg-white/70 px-2 py-0.5 border border-current/10">{term}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* URL Sources */}
                  {URL_CATEGORIES.map(cat => {
                    const urls = terms[cat.key] || [];
                    if (urls.length === 0) return null;
                    return (
                      <div key={cat.key} className="border border-zinc-200 bg-zinc-50 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Link size={14} className="text-zinc-500" />
                          <span className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">{cat.label}</span>
                          <span className="text-[10px] text-zinc-400">{urls.length}</span>
                        </div>
                        <div className="space-y-1">
                          {urls.map((url, i) => (
                            <div key={i} className="text-xs text-zinc-500 truncate font-mono">{url}</div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contact Page Patterns */}
      <div className="border border-zinc-200 bg-white p-5 mb-4" data-testid="contact-patterns">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-indigo-600" />
          <h3 className="text-sm font-semibold">Contact Pagina Patronen</h3>
          <span className="text-[10px] text-zinc-400">URL-paden die worden gescand voor contactinformatie</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {contactPatterns.map((p, i) => (
            <span key={i} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5">{p}</span>
          ))}
        </div>
      </div>

      {/* Facility Types */}
      <div className="border border-zinc-200 bg-white p-5" data-testid="facility-types">
        <div className="flex items-center gap-2 mb-3">
          <Buildings size={16} className="text-emerald-600" />
          <h3 className="text-sm font-semibold">Faciliteitstype Herkenning</h3>
          <span className="text-[10px] text-zinc-400">Trefwoorden die bepalen welk type faciliteit het is</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(facilityTypes).map(([type, keywords]) => (
            <div key={type} className="border border-zinc-100 p-3">
              <div className="text-xs font-semibold text-zinc-700 mb-1.5 capitalize">{type.replace(/_/g, ' ')}</div>
              <div className="flex flex-wrap gap-1">
                {keywords.map((kw, i) => (
                  <span key={i} className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">{kw}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
